import { OndeFicarWidget } from '@/components/OndeFicarWidget';

export const frontmatter = {
  category: 'atracao',
  title: 'Enchanted Cave: Legend, Trail, and How to Visit the Most Mystical Attraction on Ilha do Mel (Honey Island)',
  excerpt: 'Complete guide to the Enchanted Cave on Ilha do Mel: the legend of the Caingangue mermaids, a unique geological formation, how to get there via the Enchanted trail, and the ideal time to visit during low tide.',
  publishedAt: '2026-04-24',
  author: 'Guia Ilha do Mel',
  tags: ['gruta-das-encantadas', 'encantadas', 'lenda', 'trilha', 'natureza'],
  coverImage: '/images/gruta-das-encantadas.jpg',
  readingTime: '7 min',
};

# Enchanted Cave

The Gruta das Encantadas (Enchanted Cave) is the **most important natural heritage site on Ilha do Mel** — and also the most mystical. Formed over thousands of years by the action of waves on volcanic rocks, the cave carries a legend that locals still tell today: the story of the mermaids who inhabited its waters and bewitched fishermen with their song.

In this guide, we tell the legend, explain the geology, show you how to get there, and give you all the tips to live this unique experience.

---

The Legend of the Enchanted Ones

According to the legend of the Kaingang Indians, mermaids inhabited the grotto. Daughters of Acauã and Jurema, a cursed couple from enemy tribes, they danced and sang at dawn and dusk. Their song bewitched fishermen, who lost their way and shipwrecked on the rocks. A young Indian fell in love with a mermaid with green eyes; to be together, he had to die. Hand in hand, they disappeared into the deep waters.

The legend explains the village's name — **Encantadas** — and the reason why the inhabitants treat the cave with such respect. For the Kaingang people, the cave was a sacred place, inhabited by spirits that should not be disturbed.

Today, the legend of the Encantadas is told to visitors as part of the experience. Many say that, in the silence of the morning, it is still possible to hear the echo of the waves crashing against the rocks as if they were "songs" coming from the bottom of the sea.

---

Geological formation: how nature sculpted the cave

The Gruta das Encantadas (Enchanted Cave) is not just beautiful — it's a geological monument. Its formation began millions of years ago when volcanic magma solidified into rocks called diabase, which are weaker than the migmatite (metamorphic rock) that surrounds it.

Over time, the sea did its work:

- The waves repeatedly crashed against the diabase rocks.

- Differential erosion created an opening approximately **10 meters long**.

- The result is a natural cave that can be explored inside during low tide.

This combination of migmatite and diabase is rare on the Brazilian coast, making Gruta das Encantadas a heritage site not only for the island, but for national geology.

---

How to get to the Enchanted Cave

Access is simple and quick — perfect for all ages:

| Information | Details |

| -------------------- | -------------------------- |

| **Starting Point** | Encantadas Village |

| **Distance** | 600 meters of trail |

| **Duration** | 10–15 minutes of walking |

| **Difficulty** | Easy |

| **Last 100m** | Wooden walkway |

Step by step

1. **Leave the village of Encantadas** via the coastal trail (follow the signs or ask any local resident)
2. **Walk 500m** along a flat trail through the native forest
3. **Last 100m** — descend via the wooden walkway (it may be slippery)
4. **You've arrived!** The cave will be in front of you, among the rocks

**Tip:** Wear sneakers with rubber soles. The wooden walkway gets damp and slippery, especially early in the morning.

---

When to visit: the tide makes all the difference.

The Gruta das Encantadas (Enchanted Cave) can only be visited during low tide. When the tide rises, the cave entrance becomes submerged and inaccessible.

| Best time | Why |

| ---------------------------- | ----------------------------------------------------------- |

| **Morning (8am–1pm)** | This is generally when the tide is low in Encantadas |

| **Consult the tide table** | Use the Navy's website to confirm the exact time of day |

**Don't go without checking:** the tide changes every day. The difference between high and low tide on the island can reach 1.5m — enough to completely change access.

---

What to expect during your visit

Inside the cave, you will find:

- **Dark rock walls** with unique textures formed by erosion

- **Natural pools** that form between the rocks at low tide

- **Sound effect** — the sound of the waves echoes in a peculiar way inside the cave

- **Incredible photography** — sunlight filtered through the opening creates dramatic contrasts

**Bring a flashlight:** the inside of the cave is dark, and a flashlight helps to see details of the rock formations.

---

Combine with the Mermaid Basin

The **Mermaid's Basin** is just a 15-minute hike beyond the Enchanted Cave. It's a natural formation where the sea has created a "pool" among the rocks — perfect for a relaxing swim and photos.

The combination of Gruta (Cave) and Bacia da Sereia (Mermaid's Basin) is the classic morning itinerary in Encantadas. Leave early, visit the cave at low tide, then head to Bacia da Sereia and finish with lunch at Restaurante Fim da Trilha.

---

What to bring

- Non-slip sneakers

- Flashlight (to see inside the cave)

- Sunscreen

- Water
- Camera or cell phone with a charged battery
- Cash (for lunch afterwards)

---

Where to stay near Gruta das Encantadas

Since the cave is located in the village of Encantadas, the best option is to stay nearby. This way you can leave early and catch the low tide without rushing.

<OndeFicarWidget />

---

Still have questions?

The Gruta das Encantadas (Enchanted Cave) is just one of the wonders of Ilha do Mel (Honey Island). If you want to explore the village's beaches, find out how to get there, and discover where to eat, check out our **Complete Guide to [Ilha do Mel](/)** — we've gathered everything you need to know in one place.